import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useMatch, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../data";
import { baseRateWarnings, fxWarning } from "./model";
import { useAccount } from "../../app/session";
import { CreatorOfficeLogo, RahmenKontext } from "../../components/shell/Logo";
import { Toasts } from "../../components/Toast";
import { toggleTheme } from "../../lib/theme";
import { today as heuteLokal } from "../../lib/format";
import { t } from "../../lib/sprache";
import { setManifest } from "../../pwa";
import "./betrieb.css";

/**
 * Rahmen der Betreiber-Konsole (17_betreiber/*.html): Kopfzeile wie die öffentlichen Seiten (header.webnav),
 * darunter main.auth-shell mit der Bereichsnavigation (nav.settingsnav) und dem festen Hinweis des Pakets.
 * Zusätzlich zur Vorlage: angemeldete Adresse und Abmelden (die Konsole hat sonst keinen Ausgang).
 */
export function BetriebRahmen() {
  const { pathname } = useLocation();
  const detail = useMatch("/betrieb/agenturen/:orgId");
  const { account } = useAccount();
  const [busy, setBusy] = useState(false);
  const path = pathname.replace(/\/+$/, "");
  const listCurrent = path === "/betrieb/agenturen";
  const startCurrent = path === "/betrieb" || path === "/betrieb/";
  const supportCurrent = path === "/betrieb/support";
  const platformCurrent = path === "/betrieb/plattform";
  const requestsCurrent = path === "/betrieb/antraege";
  const opsCurrent = path === "/betrieb/ueberwachung";
  const navigate = useNavigate();
  // Eigene App „CreatorOffice Konsole“ (Inhaber 24.10.2026): Manifest, Symbol und Name für „Zum Dock hinzufügen“
  useEffect(() => {
    setManifest("/betrieb");
    return () => setManifest(window.location.pathname);
  }, []);
  const unter = (p: string) => pathname.startsWith(p);
  // Inhaber 20.10.2026: übersichtlicher; die Bereiche stehen in drei benannten Gruppen statt 14 gleichwertiger Knöpfe
  type Bereich = { to: string; label: string; current: boolean };
  const gruppen: { label: string; bereiche: Bereich[] }[] = [
    { label: t("Kunden", "Customers"), bereiche: [
      { to: "/betrieb", label: "Übersicht", current: startCurrent },
      { to: "/betrieb/agenturen", label: "Agenturen", current: listCurrent },
      ...(detail ? [{ to: detail.pathname, label: "Agentur-Detail", current: true }] : []),
      { to: "/betrieb/antraege", label: "Anträge", current: requestsCurrent },
      { to: "/betrieb/demo-termine", label: t("Demo-Termine", "Demo bookings"), current: unter("/betrieb/demo-termine") },
    ] },
    { label: t("Support", "Support"), bereiche: [
      { to: "/betrieb/tickets", label: "Tickets", current: unter("/betrieb/tickets") },
      { to: "/betrieb/fehler", label: "Gemeldete Fehler", current: unter("/betrieb/fehler") },
      { to: "/betrieb/support", label: "Support-Zugang", current: supportCurrent },
      { to: "/betrieb/ki-protokoll", label: "KI-Protokoll", current: unter("/betrieb/ki-protokoll") },
    ] },
    { label: t("Plattform", "Platform"), bereiche: [
      { to: "/betrieb/ueberwachung", label: t("Betrieb", "Operations"), current: opsCurrent },
      { to: "/betrieb/plattform", label: "Produkt-Standards", current: platformCurrent },
      { to: "/betrieb/rabattcodes", label: "Rabattcodes", current: unter("/betrieb/rabattcodes") },
      { to: "/betrieb/kuendigungsweg", label: "Kündigungsweg", current: unter("/betrieb/kuendigungsweg") },
      { to: "/betrieb/rechtstexte", label: "Rechtstexte", current: unter("/betrieb/rechtstexte") },
      { to: "/betrieb/ki-zugaenge", label: "KI-Zugänge", current: unter("/betrieb/ki-zugaenge") },
      { to: "/betrieb/instagram", label: "Instagram", current: unter("/betrieb/instagram") },
    ] },
  ];
  const bereiche = gruppen.flatMap((g) => g.bereiche);
  const aktuell = bereiche.find((b) => b.current);
  // Wechselkurse (0076): Warnung, wenn der tägliche Abruf der EZB-Kurse ausbleibt
  const fx = useQuery({ queryKey: ["fx_rates", "letzter", "betrieb"], staleTime: 60 * 60_000,
    queryFn: () => api.select<{ valid_on: string }>("fx_rates", { columns: "valid_on", order: [["valid_on", "desc"]], limit: 1 }) });
  const today = heuteLokal();   // nicht UTC (Gesamtprüfung 07.10.2026)
  const fxText = fx.data ? fxWarning(fx.data[0]?.valid_on ?? null, today) : null;
  // Basiszinssätze (SICHT-OEFFENTLICH-BETRIEB-19): fehlend oder vor dem laufenden Halbjahr
  const baseRates = useQuery({ queryKey: ["platform_base_rates"], queryFn: () => api.select<{ country: string; valid_from: string }>("platform_base_rates", { order: [["country", "asc"], ["valid_from", "desc"]] }) });
  const rateRules = useQuery({ queryKey: ["platform_interest_rules"], queryFn: () => api.select<{ country: string; mode: string }>("platform_interest_rules", { order: [["country", "asc"]] }) });
  const rateTexts = baseRates.data && rateRules.data ? baseRateWarnings(rateRules.data, baseRates.data, today) : [];

  async function signOut() {
    setBusy(true);
    try {
      await api.signOut();
      // K-4: in der Dock-App wieder die Betreiber-Anmeldung, danach zurück in die Konsole
      window.location.assign("/anmelden?betrieb=1&weiter=%2Fbetrieb");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <a className="skip btn" href="#main">Zum Inhalt</a>
      <header className="webnav">
        <Link className="brand co-brand" to="/betrieb">
          <CreatorOfficeLogo form="responsive" />
        </Link>
        <nav aria-label="Konto">
          {account && <small>{account.session.email}</small>}
          {/* 0330: Weg zurück in die eigene Agentur bzw. ins Creator-Portal (Startseite nach dem Login ist für Admins der Betrieb) */}
          {account && account.memberships.length > 0 && <Link className="btn" to="/agentur">{t("Zur Agentur", "To agency")}</Link>}
          {account && account.memberships.length === 0 && account.talentLinks.length > 0 && <Link className="btn" to="/creator">{t("Zum Creator-Portal", "To creator portal")}</Link>}
          <button className="" onClick={toggleTheme}>Hell / Dunkel</button>
          <button className="" onClick={() => void signOut()} disabled={busy} aria-busy={busy}>{busy ? "Wird abgemeldet …" : "Abmelden"}</button>
        </nav>
      </header>
      <main id="main" className="auth-shell stack tk-betrieb">
        {/* Prüf-Chat Traffic (16.10.2026): am Handy füllten die Knöpfe fast einen Bildschirm; dort eine Auswahl wie in der Rechnungsliste, am Desktop unverändert */}
        <nav className="tk-betrieb-nav" aria-label="Betreiber-Konsole">
          {gruppen.map((g) => (
            <div key={g.label} className="tk-betrieb-gruppe" role="group" aria-label={g.label}>
              <span className="tk-betrieb-gruppe-titel" aria-hidden="true">{g.label}</span>
              <div className="settingsnav">
                {g.bereiche.map((b) => <Link key={b.to} to={b.to} className={b.current ? "current" : undefined} aria-current={b.current ? "page" : undefined}>{b.label}</Link>)}
              </div>
            </div>
          ))}
        </nav>
        <label className="tk-betrieb-navwahl">
          <span className="sr-only">{t("Bereich der Betreiber-Konsole", "Operator console section")}</span>
          <select value={aktuell?.to ?? ""} onChange={(e) => { if (e.target.value) navigate(e.target.value); }}>
            {!aktuell && <option value="">{t("Bereich wählen", "Choose section")}</option>}
            {gruppen.map((g) => <optgroup key={g.label} label={g.label}>{g.bereiche.map((b) => <option key={b.to} value={b.to}>{b.label}</option>)}</optgroup>)}
          </select>
        </label>
        <p className="tk-betrieb-protokoll">{t("Plattform-Admin · Zugriffe des Betreibers und der KI werden protokolliert", "Platform admin · operator and AI access is logged")}</p>
        {fxText && <div className="notice warning" role="alert">{fxText}</div>}
        {rateTexts.map((x) => <div key={x} className="notice warning" role="alert">{x}</div>)}
        <RahmenKontext.Provider value={true}><Outlet /></RahmenKontext.Provider>
      </main>
      <Toasts />
    </>
  );
}
